import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AppMode = "classic" | "game" | "workflow" | "space" | "mission";

export type Agent = {
  id: string;
  name: string;
  role: string;
  models: string[]; // multi-select
  ensemble: boolean;
  status: "active" | "paused" | "idle";
  calls: number;
  success: number;
  // game mode props
  x: number;
  y: number;
  color: string;
  emoji: string;
};

export type Schedule = {
  id: string;
  agentId: string;
  cron: string; // human readable
  prompt: string;
  enabled: boolean;
  nextRun: number; // ms ts
};

export type MemoryItem = {
  id: string;
  kind: "note" | "vector" | "fact" | "lesson";
  title: string;
  body: string;
  tags: string[];
  createdAt: number;
  importance: number; // 0-100
};

export type SandboxRun = {
  id: string;
  tool: string;
  input: string;
  output: string;
  status: "ok" | "error" | "running";
  ms: number;
  hash: string; // sha-512 prefix
  ts: number;
};

type State = {
  mode: AppMode;
  setMode: (m: AppMode) => void;

  agents: Agent[];
  setAgentModels: (id: string, models: string[]) => void;
  toggleEnsemble: (id: string) => void;
  toggleAgentStatus: (id: string) => void;
  addAgent: (a: Omit<Agent, "id">) => void;

  schedules: Schedule[];
  addSchedule: (s: Omit<Schedule, "id">) => void;
  toggleSchedule: (id: string) => void;
  removeSchedule: (id: string) => void;

  memory: MemoryItem[];
  addMemory: (m: Omit<MemoryItem, "id" | "createdAt">) => void;
  removeMemory: (id: string) => void;

  sandboxRuns: SandboxRun[];
  pushRun: (r: Omit<SandboxRun, "id" | "ts">) => void;

  autoUpdate: boolean;
  setAutoUpdate: (v: boolean) => void;
  modelsLastUpdated: number;
  refreshModels: () => void;

  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
};

const SEED_AGENTS: Agent[] = [
  { id: "orch", name: "Orchestrator-Prime", role: "Master coordinator", models: ["gpt-5.4", "claude-opus-4.7"], ensemble: true,  status: "active", calls: 12482, success: 99.2, x: 0.5, y: 0.5, color: "#22d3ee", emoji: "🧠" },
  { id: "scout",name: "Scout-Δ",            role: "Web crawling",       models: ["gemini-3-flash"],              ensemble: false, status: "active", calls: 8421,  success: 97.8, x: 0.2, y: 0.3, color: "#a78bfa", emoji: "🛰️" },
  { id: "synth",name: "Synth-Ω",            role: "Synthesis",          models: ["claude-opus-4.7", "deepseek-v4"], ensemble: true, status: "active", calls: 6210, success: 98.4, x: 0.8, y: 0.3, color: "#f472b6", emoji: "⚗️" },
  { id: "crit", name: "Critic-Λ",           role: "Adversarial",        models: ["grok-4.20", "kimi-k2-thinking"], ensemble: true, status: "paused", calls: 4128, success: 94.1, x: 0.2, y: 0.7, color: "#facc15", emoji: "⚖️" },
  { id: "scrb", name: "Scribe-Ψ",           role: "Composition",        models: ["claude-sonnet-4.6"],            ensemble: false,status: "active", calls: 3982, success: 99.7, x: 0.8, y: 0.7, color: "#34d399", emoji: "✍️" },
  { id: "sent", name: "Sentinel-Σ",         role: "Security",           models: ["llama-4-maverick"],             ensemble: false,status: "idle",   calls: 1284, success: 99.9, x: 0.5, y: 0.85,color: "#f87171", emoji: "🛡️" },
];

export const useNeural = create<State>()(
  persist(
    (set) => ({
      mode: "classic",
      setMode: (m) => set({ mode: m }),

      agents: SEED_AGENTS,
      setAgentModels: (id, models) =>
        set((s) => ({ agents: s.agents.map((a) => (a.id === id ? { ...a, models } : a)) })),
      toggleEnsemble: (id) =>
        set((s) => ({ agents: s.agents.map((a) => (a.id === id ? { ...a, ensemble: !a.ensemble } : a)) })),
      toggleAgentStatus: (id) =>
        set((s) => ({
          agents: s.agents.map((a) =>
            a.id === id ? { ...a, status: a.status === "active" ? "paused" : "active" } : a,
          ),
        })),
      addAgent: (a) =>
        set((s) => ({ agents: [...s.agents, { ...a, id: crypto.randomUUID().slice(0, 8) }] })),

      schedules: [
        { id: "s1", agentId: "scout", cron: "Every day 09:00", prompt: "Crawl arXiv for new AI safety papers", enabled: true, nextRun: Date.now() + 3600_000 },
        { id: "s2", agentId: "synth", cron: "Every Mon 08:30", prompt: "Weekly digest synthesis",              enabled: true, nextRun: Date.now() + 86400_000 },
      ],
      addSchedule: (s) =>
        set((st) => ({ schedules: [...st.schedules, { ...s, id: crypto.randomUUID().slice(0, 6) }] })),
      toggleSchedule: (id) =>
        set((st) => ({ schedules: st.schedules.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)) })),
      removeSchedule: (id) =>
        set((st) => ({ schedules: st.schedules.filter((x) => x.id !== id) })),

      memory: [
        { id: "m1", kind: "lesson", title: "Avoid double-summarization on long arXiv pdfs", body: "Switching from 2-pass to 1-pass with chunk overlap reduced cost 38% with no quality loss.", tags: ["arxiv","cost"], createdAt: Date.now()-86400000, importance: 88 },
        { id: "m2", kind: "fact",   title: "User prefers Portuguese for chat, English for code", body: "Detected from last 14 sessions.", tags: ["pref","language"], createdAt: Date.now()-172800000, importance: 72 },
        { id: "m3", kind: "vector", title: "Embedding cluster: AI governance",                  body: "1,284 docs · centroid stable for 7d.",                       tags: ["governance"], createdAt: Date.now()-3600000, importance: 64 },
        { id: "m4", kind: "note",   title: "Idea: cross-agent debate before commit",            body: "Try Critic-Λ + Synth-Ω debate on every plan over $5.",       tags: ["idea"], createdAt: Date.now()-7200000, importance: 55 },
      ],
      addMemory: (m) =>
        set((s) => ({
          memory: [{ ...m, id: crypto.randomUUID().slice(0, 8), createdAt: Date.now() }, ...s.memory],
        })),
      removeMemory: (id) => set((s) => ({ memory: s.memory.filter((x) => x.id !== id) })),

      sandboxRuns: [],
      pushRun: (r) =>
        set((s) => ({
          sandboxRuns: [{ ...r, id: crypto.randomUUID().slice(0, 8), ts: Date.now() }, ...s.sandboxRuns].slice(0, 200),
        })),

      autoUpdate: true,
      setAutoUpdate: (v) => set({ autoUpdate: v }),
      modelsLastUpdated: Date.now(),
      refreshModels: () => set({ modelsLastUpdated: Date.now() }),

      paletteOpen: false,
      setPaletteOpen: (v) => set({ paletteOpen: v }),
    }),
    {
      name: "neural-os-v18",
      partialize: (s) => ({
        agents: s.agents,
        schedules: s.schedules,
        memory: s.memory,
        autoUpdate: s.autoUpdate,
        modelsLastUpdated: s.modelsLastUpdated,
        mode: s.mode,
      }),
    },
  ),
);

// SHA-512 helper para sandbox audit trail
export async function sha512(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-512", buf);
  const arr = Array.from(new Uint8Array(hash));
  return arr.map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
}
